import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../../components/custom-table/custom-table";
import NarAgentGridBulkToggleModal from "../../../../../components/modals/narAgentGridBulkToggleModal/narAgentGridBulkToggleModal";
import ToggleSwitchConfirmationDialog from "../../../../../components/modals/ToggleSwitchConfirmationDialog/ToggleSwitchConfirmationDialog";
import { MEDICAL_PA_BATCH_SIZE } from "../../../../../constants/medicalPAbatch";
import { useTablesContext } from "../../../../../context/tablesContextProvider";
import { NarAgentGridData } from "../../../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../../../enums/modalId";
import { NarAgentGridTableColumnKeys } from "../../../../../enums/tableColumnKeys";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { useNarAgentGridBulkToggleConfirm } from "../hooks/useNarAgentGridBulkToggleConfirm";
import { useNarAgentGridSelectAll } from "../hooks/useNarAgentGridSelectAll";
import { useNarAgentGridToggle } from "../hooks/useNarAgentGridToggle";
import { NarAgentGridBulkToggleAction } from "../utils/narAgentGridBulkToggleActions";
import {
  generateNarAgentGridTableData,
  NarAgentGridTableHeaders,
} from "../utils/narAgentGridTable";
import NarAgentGridBulkToggleMenu from "./narAgentGridBulkToggleMenu";

const NarAgentGridTable = ({
  narAgentGridTableRef,
  exportingInProgress,
  apiData,
  clientId,
  onAuthGridRefetch,
}: {
  narAgentGridTableRef: React.RefObject<HTMLDivElement>;
  exportingInProgress: boolean;
  apiData: NarAgentGridData[];
  clientId: string;
  onAuthGridRefetch: () => Promise<void>;
}) => {
  const [tableData, setTableData] = useState<any>([]);

  const { openedModalId, metaData: modalMetaData } = useSelector(
    (state: RootState) => state.modal,
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const dispatch = useDispatch<AppDispatch>();

  const { setTableDataForContext } = useTablesContext();

  const {
    handleToggleChange,
    updateToggleStateForRow,
    toggleSwitchData,
    setToggleSwitchData,
  } = useNarAgentGridToggle(tableData, setTableData, clientId);

  const {
    handleSelectAll,
    toggleCheckboxSelection,
    isCheckboxSelected,
    hasSelectedRows,
    selectedRowCount,
  } = useNarAgentGridSelectAll(tableData, setTableData);

  const bulkToggleAction = modalMetaData?.action as
    NarAgentGridBulkToggleAction | undefined;

  const { bulkToggleLoading, handleBulkToggleModalConfirm } =
    useNarAgentGridBulkToggleConfirm({
      tableData,
      clientId,
      action: bulkToggleAction,
      onAuthGridRefetch,
    });

  const handleBulkToggleAction = (action: NarAgentGridBulkToggleAction) => {
    dispatch(
      setOpenedModalId({
        id: ModalId.NAR_AGENT_GRID_BULK_TOGGLE_MODAL,
        metaData: {
          action,
          selectedCount: selectedRowCount,
        },
      }),
    );
  };

  const handleBulkToggleModalClose = () => {
    if (bulkToggleLoading) {
      return;
    }
    dispatch(closeModal());
  };

  const exportDataForTable = () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.ANALYTICS_DATE_PICKER_MODAL,
      }),
    );
  };
  const docSize = MEDICAL_PA_BATCH_SIZE;

  useEffect(() => {
    setTableData(generateNarAgentGridTableData(apiData, authStatusOptions));
  }, [apiData, authStatusOptions]);

  const handleEndIndexOfTable = (endIndex: number) => {
    const batchNumber = Math.ceil(tableData.length / docSize);
    setTableDataForContext("narAgentGrid", {
      lastBatchFetched: batchNumber,
    });
  };

  const handleModalCancel = () => {
    if (toggleSwitchData) {
      const revertedData = {
        ...toggleSwitchData,
        value: !toggleSwitchData.value,
      };
      updateToggleStateForRow(
        revertedData,
        NarAgentGridTableColumnKeys.AgentMode,
      );
    }
    dispatch(closeModal());
    setToggleSwitchData(null);
  };

  const handleModalConfirm = () => {
    dispatch(closeModal());
    setToggleSwitchData(null);
  };

  useEffect(() => {
    if (openedModalId === ModalId.TOGGLE_SWITCH_CONFIRMATION_DIALOG) {
      openModal(ModalId.TOGGLE_SWITCH_CONFIRMATION_DIALOG);
    }
    if (openedModalId === ModalId.NAR_AGENT_GRID_BULK_TOGGLE_MODAL) {
      openModal(ModalId.NAR_AGENT_GRID_BULK_TOGGLE_MODAL);
    }
  }, [openedModalId]);

  return (
    <>
      <div
        ref={narAgentGridTableRef}
        className="nar-agent-grid__table-container flex h-full flex-col overflow-auto"
      >
        <CustomTable
          tableHeaders={NarAgentGridTableHeaders}
          tableData={tableData}
          tableName={"narAgentGrid"}
          itemsPerPage={tableData?.length ?? 0}
          pagesPerView={4}
          count={tableData.length}
          totalCount={tableData.length}
          endIndexOfTable={handleEndIndexOfTable}
          searchingText={() => {}}
          hideSearchBar={true}
          isFetching={false || (false && tableData?.length === 0)}
          showExportButton={false}
          onExportButtonClick={exportDataForTable}
          showInLineLoader={false}
          showRefreshButton={false}
          hidePagination={true}
          onRefreshButton={() => {}}
          exportingInProgress={exportingInProgress}
          onChangeEmit={handleToggleChange}
          onSelectAll={handleSelectAll}
          toggleCheckboxSelection={toggleCheckboxSelection}
          isCheckboxSelected={isCheckboxSelected}
          headerChildren={
            <NarAgentGridBulkToggleMenu
              disabled={!hasSelectedRows}
              onAction={handleBulkToggleAction}
            />
          }
        />
      </div>
      {openedModalId === ModalId.TOGGLE_SWITCH_CONFIRMATION_DIALOG && (
        <ToggleSwitchConfirmationDialog
          onClose={handleModalCancel}
          onConfirm={handleModalConfirm}
          clientId={clientId}
          metaData={
            toggleSwitchData
              ? {
                  id: toggleSwitchData.id,
                  switchState: toggleSwitchData.value,
                }
              : undefined
          }
        />
      )}
      {openedModalId === ModalId.NAR_AGENT_GRID_BULK_TOGGLE_MODAL && (
        <NarAgentGridBulkToggleModal
          onClose={handleBulkToggleModalClose}
          loading={bulkToggleLoading}
          metaData={
            modalMetaData
              ? {
                  action: modalMetaData.action as NarAgentGridBulkToggleAction,
                  selectedCount: modalMetaData.selectedCount as number,
                }
              : undefined
          }
          handleSave={handleBulkToggleModalConfirm}
        />
      )}
    </>
  );
};

export default NarAgentGridTable;
